<template>
  <v-dialog v-model="open" max-width="760" persistent scrollable>
    <v-card class="kf-tpl">
      <v-card-title class="kf-tpl__title">
        <LucideIcon :name="editing ? 'pencil' : 'plus'" :size="20" class="mr-2" />
        {{ heading }}
        <v-spacer />
        <StatusChip
          v-if="editing"
          :status="editing.status"
          :color="statusColor[editing.status]"
          small
        />
      </v-card-title>

      <v-card-text class="kf-tpl__body">
        <!-- Saving a live template changes what borrowers get from then on.
             Said before the form, because it changes what someone is about
             to do. -->
        <v-alert
          v-if="editing && editing.status === 'active'"
          dense
          text
          type="info"
          class="kf-tpl__note"
        >
          This template is <strong>live</strong>. Your changes are used as soon as you
          save; if something it needs goes missing, it turns inactive and says why.
        </v-alert>

        <!-- Opened from the Agent Tracker's missing templates: say where the
             text came from and what it still needs, before anyone edits it. -->
        <v-alert v-if="fromDraft" dense text type="info" class="kf-tpl__note">
          <template v-if="fromDraft.body">
            Drafted by the growth agent for a missing template.
            <span v-if="fromDraft.why">{{ fromDraft.why }}</span>
            Check the words before saving.
          </template>
          <template v-else>
            Filled in for a missing template; write the message yourself.
          </template>
          <span v-if="fromDraft.too_long">
            The SMS is over 160 characters; shorten it before registering.
          </span>
          <span v-if="fromDraft.dropped_placeholders && fromDraft.dropped_placeholders.length">
            Removed blanks the agent cannot fill:
            {{ fromDraft.dropped_placeholders.map((v) => "{" + v + "}").join(", ") }}.
          </span>
        </v-alert>

        <v-form ref="form" @submit.prevent>
          <div class="kf-grid">
            <div class="kf-field">
              <span class="kf-field__label">Name</span>
              <v-text-field
                v-model="form.name"
                :rules="$rules.REQURIED_FILED"
                :disabled="!!editing"
                hide-details="auto"
                dense
                outlined
                placeholder="offer-expiry-day-7-sms"
              />
              <span class="kf-hint">
                Include the channel — the same step exists on three channels with
                different copy.
              </span>
            </div>

            <div class="kf-field">
              <span class="kf-field__label">Channel</span>
              <v-select
                v-model="form.channel"
                :items="channels"
                item-text="label"
                item-value="value"
                :rules="$rules.REQURIED_FILED"
                :disabled="!!editing"
                hide-details="auto"
                dense
                outlined
              />
              <span v-if="editing" class="kf-hint">
                The channel is part of what the copy is registered as, so it is
                fixed once a template exists.
              </span>
            </div>

            <div class="kf-field">
              <span class="kf-field__label">Intent</span>
              <v-select
                v-model="form.intent"
                :items="intents"
                :rules="$rules.REQURIED_FILED"
                hide-details="auto"
                dense
                outlined
              />
              <span class="kf-hint">What the message is for. The agent filters on this.</span>
            </div>

            <div class="kf-field">
              <span class="kf-field__label">Language</span>
              <v-text-field
                v-model="form.language"
                hide-details="auto"
                dense
                outlined
                placeholder="en"
              />
            </div>

            <div class="kf-field kf-field--wide">
              <span class="kf-field__label">Stages</span>
              <v-select
                v-model="form.stage_tags"
                :items="stages"
                multiple
                chips
                small-chips
                deletable-chips
                hide-details="auto"
                dense
                outlined
              />
              <span class="kf-hint">
                Leave empty and this copy suits every stage.
              </span>
            </div>

            <!-- What the growth agent matches on. Without a purpose it never
                 picks the template. -->
            <div class="kf-field kf-field--wide">
              <span class="kf-field__label">Borrower types</span>
              <v-select
                v-model="form.personas"
                :items="borrowerTypes"
                item-text="label"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                hide-details="auto"
                dense
                outlined
              />
              <span class="kf-hint">Who the growth agent may send this to. Empty means any type.</span>
            </div>

            <div class="kf-field">
              <span class="kf-field__label">Purpose</span>
              <v-select
                v-model="form.purpose"
                :items="purposes"
                item-text="label"
                item-value="value"
                clearable
                hide-details="auto"
                dense
                outlined
              />
              <span class="kf-hint">The growth agent only picks templates with a purpose.</span>
            </div>

            <div class="kf-field">
              <span class="kf-field__label">Tone</span>
              <v-select
                v-model="form.tone"
                :items="tones"
                item-text="label"
                item-value="value"
                clearable
                hide-details="auto"
                dense
                outlined
              />
            </div>

            <div class="kf-field">
              <span class="kf-field__label">Family</span>
              <v-text-field
                v-model="form.family"
                hide-details="auto"
                dense
                outlined
                placeholder="gms_after_loan_1"
              />
              <span class="kf-hint">The same message on other channels shares one family.</span>
            </div>

            <!-- Push and Email carry a headline; SMS and WhatsApp do not. -->
            <div v-if="hasSubject" class="kf-field kf-field--wide">
              <span class="kf-field__label">Subject / title</span>
              <v-text-field
                v-model="form.subject"
                hide-details="auto"
                dense
                outlined
              />
            </div>

            <div class="kf-field kf-field--wide">
              <span class="kf-field__label">Message</span>
              <v-textarea
                v-model="form.body"
                :rules="$rules.REQURIED_FILED"
                rows="5"
                hide-details="auto"
                dense
                outlined
                @input="syncVariables"
              />
              <span class="kf-hint">
                Placeholders are single-brace: <code>{name}</code>,
                <code>{url}</code>, <code>{lender}</code> are filled in for you.
                Anything else you use has to be listed below.
              </span>
            </div>

            <!-- Channel-specific registration. Shown only where it applies,
                 because a DLT id on a Push template is a field nobody can fill. -->
            <div v-if="isSms" class="kf-field kf-field--wide">
              <span class="kf-field__label">DLT template id</span>
              <v-text-field
                v-model="form.dlt_id"
                hide-details="auto"
                dense
                outlined
                placeholder="1207176182287692965"
              />
              <span class="kf-hint">
                Required before this can go active. Indian SMS is DLT-regulated
                and the provider rejects an unregistered body — a draft may wait
                here until the id comes through.
              </span>
            </div>

            <div v-if="isWhatsapp" class="kf-field kf-field--wide">
              <span class="kf-field__label">WhatsApp template name</span>
              <v-text-field
                v-model="form.whatsapp_template_name"
                hide-details="auto"
                dense
                outlined
                placeholder="karrofin_welcome"
              />
              <span class="kf-hint">
                Required before this can go active, and must be a name Meta has
                approved. An unapproved name is accepted by the provider and
                delivered to nobody.
              </span>
            </div>

            <div class="kf-field kf-field--wide">
              <span class="kf-field__label">Variables</span>
              <v-combobox
                v-model="form.variables_schema"
                :items="detected"
                multiple
                chips
                small-chips
                deletable-chips
                hide-details="auto"
                dense
                outlined
                placeholder="Type a name and press enter"
              />
              <span v-if="undeclared.length" class="kf-warn">
                Used in the message but not listed:
                {{ undeclared.map((v) => "{" + v + "}").join(", ") }}
              </span>
              <span v-else class="kf-hint">
                What the agent will be asked to fill in. Without braces.
              </span>
            </div>
          </div>

          <div v-if="warnings.length" class="kf-warnbox">
            <div v-for="(w, i) in warnings" :key="i">{{ w }}</div>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="kf-tpl__actions">
        <v-spacer />
        <v-btn text small class="text-none" :disabled="saving" @click="close">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          depressed
          small
          class="text-none"
          :loading="saving"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Create or edit one library template.
 *
 * Two things this form is careful about.
 *
 * **There is no status control.** The server decides: a complete template is
 * live as soon as it is saved, and one missing something (a DLT id, a
 * registered WhatsApp name, a purpose ...) is saved inactive with the reasons,
 * which the snackbar repeats. Editing changes the template in place.
 *
 * **The channel decides the fields.** A DLT id means nothing on Push and a
 * WhatsApp name means nothing on SMS, so each appears only where it can be
 * filled in. Both are optional here even where they apply: the registration
 * arrives weeks after the copy does, and refusing the draft would leave nowhere
 * to record the backlog.
 */
import StatusChip from "@/components/karrofin/StatusChip.vue";
import api from "@/app-js/karrofin/api";
import {
  BORROWER_TYPES,
  CHANNELS,
  TEMPLATE_INTENTS,
  TEMPLATE_PURPOSES,
  TEMPLATE_STAGES,
  TEMPLATE_STATUS_COLOR,
  TEMPLATE_TONES,
} from "@/app-js/karrofin/constants";

/** Single-brace placeholders, the syntax the server renders with. */
const PLACEHOLDER = /\{([a-z_][a-z0-9_]*)\}/g;

/** Filled in by the engine for every message; never the author's to declare. */
const AUTOMATIC = ["name", "url", "lender"];

function emptyForm() {
  return {
    name: "",
    channel: "Push",
    intent: "",
    language: "en",
    stage_tags: [],
    subject: "",
    body: "",
    dlt_id: "",
    whatsapp_template_name: "",
    variables_schema: [],
    personas: [],
    purpose: "",
    tone: "",
    family: "",
  };
}

export default {
  name: "TemplateFormDialog",
  components: { StatusChip },
  props: {
    value: { type: Boolean, default: false },
    /**
     * The template to edit, or null to create.
     *
     * Read when the dialog OPENS, not watched: a background list refresh must
     * not rewrite what someone is halfway through typing. Same rule as
     * CampaignUrlDialog and ProductFormDialog.
     */
    template: { type: Object, default: null },
    /**
     * A new template's fields to start from (the Agent Tracker's suggested
     * template), used only when `template` is null. Read when the dialog opens.
     */
    draft: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      editing: null,
      fromDraft: null,
      saving: false,
      warnings: [],
      channels: CHANNELS,
      intents: TEMPLATE_INTENTS,
      stages: TEMPLATE_STAGES,
      purposes: TEMPLATE_PURPOSES,
      tones: TEMPLATE_TONES,
      borrowerTypes: BORROWER_TYPES,
      statusColor: TEMPLATE_STATUS_COLOR,
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    heading() {
      if (!this.editing) return "New template";
      return "Edit " + this.editing.name;
    },
    isSms() {
      return this.form.channel === "SMS";
    },
    isWhatsapp() {
      return this.form.channel === "Whatsapp";
    },
    hasSubject() {
      return ["Push", "Email"].indexOf(this.form.channel) !== -1;
    },
    /** Placeholders the message actually uses, minus the automatic three. */
    detected() {
      const found = [];
      let match;
      PLACEHOLDER.lastIndex = 0;
      while ((match = PLACEHOLDER.exec(this.form.body || "")) !== null) {
        if (AUTOMATIC.indexOf(match[1]) === -1 && found.indexOf(match[1]) === -1) {
          found.push(match[1]);
        }
      }
      return found;
    },
    undeclared() {
      const declared = this.form.variables_schema || [];
      return this.detected.filter((v) => declared.indexOf(v) === -1);
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.fill();
    },
  },
  /* Created already open (the library opening a handed-over draft as the page
     loads): the watcher above never sees it change, so fill here. */
  mounted() {
    if (this.value) this.fill();
  },
  methods: {
    fill() {
      const t = this.template || null;
      this.editing = t;
      this.warnings = [];
      this.form = emptyForm();
      if (t) {
        this.form.name = t.name || "";
        this.form.channel = t.channel || "Push";
        this.form.intent = t.intent || "";
        this.form.language = t.language || "en";
        this.form.stage_tags = (t.stage_tags || []).slice();
        this.form.subject = t.subject || "";
        this.form.body = t.body || "";
        this.form.dlt_id = t.dlt_id || "";
        this.form.whatsapp_template_name = t.whatsapp_template_name || "";
        this.form.variables_schema = (t.variables_schema || []).slice();
        this.form.personas = (t.personas || []).slice();
        this.form.purpose = t.purpose || "";
        this.form.tone = t.tone || "";
        this.form.family = t.family || "";
      }
      // A suggested template from the Agent Tracker: the form starts filled in.
      const d = !t ? this.draft : null;
      this.fromDraft = d;
      if (d) {
        Object.keys(this.form).forEach((key) => {
          if (d[key] === undefined || d[key] === null) return;
          this.form[key] = Array.isArray(d[key]) ? d[key].slice() : d[key];
        });
      }
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    /**
     * Offer the placeholders the body uses, without overwriting a choice.
     *
     * Additive only: someone who deliberately removed a variable should not have
     * it put back on the next keystroke.
     */
    syncVariables() {
      const declared = this.form.variables_schema || [];
      this.detected.forEach((v) => {
        if (declared.indexOf(v) === -1) declared.push(v);
      });
      this.form.variables_schema = declared.slice();
    },
    close() {
      this.open = false;
    },
    save() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      const editing = this.editing;
      const payload = {
        name: this.form.name,
        channel: this.form.channel,
        intent: this.form.intent,
        language: this.form.language || "en",
        stage_tags: this.form.stage_tags || [],
        subject: this.hasSubject ? this.form.subject : null,
        body: this.form.body,
        dlt_id: this.isSms ? this.form.dlt_id || null : null,
        whatsapp_template_name: this.isWhatsapp
          ? this.form.whatsapp_template_name || null
          : null,
        variables_schema: this.form.variables_schema || [],
        personas: this.form.personas || [],
        purpose: this.form.purpose || null,
        tone: this.form.tone || null,
        family: this.form.family || null,
      };

      Promise.resolve()
        .then(() => {
          if (editing) {
            // The name identifies the template, so it is never changed.
            delete payload.name;
            return api.updateTemplate(editing.id, payload);
          }
          return api.createTemplate(payload);
        })
        .then((result) => {
          this.warnings = result.warnings || [];
          const t = result.template || {};
          if (t.status === "active") {
            this.showSnakeBar("success", "Saved — live, the agent can use it now");
          } else {
            this.showSnakeBar(
              "error",
              "Saved, but inactive: " + (t.inactive_reasons || []).join(" ")
            );
          }
          this.$emit("saved", result.template);
          this.close();
        })
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) ||
              (editing ? "Could not update the template" : "Could not create the template")
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.kf-tpl__title {
  font-size: var(--kf-fs-title);
  font-weight: 700;
  padding-bottom: 8px;
}
.kf-tpl__body {
  max-height: 62vh;
}
.kf-tpl__note {
  font-size: var(--kf-fs-hint);
}
.kf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.kf-field--wide {
  grid-column: 1 / -1;
}
.kf-field__label {
  display: block;
  font-size: var(--kf-fs-label);
  font-weight: 600;
  color: var(--kf-text-muted);
  margin-bottom: 4px;
}
.kf-hint {
  display: block;
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  margin-top: 4px;
}
.kf-warn {
  display: block;
  font-size: var(--kf-fs-label);
  color: #C0392B;
  margin-top: 4px;
}
.kf-warnbox {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #FFF8E1;
  font-size: var(--kf-fs-label);
  color: #6D4C41;
}
.kf-tpl__actions {
  padding: 8px 16px 14px;
}
</style>
